import { Box } from '@mui/material'
import { AppV1Appseditor, AppV1Appsowner, AppV1Appsviewer } from 'c1gen/roles'
import { useRoleChecker } from 'components/no-ui/capability'
import { AppDisplayNameWithFetch } from 'components/pages/app/app-display-name'
import { RecentItemsCategory } from 'lib/constants/recent-items'
import { routes } from 'lib/routes'
import { HatGlasses, LayoutGrid, Shapes } from 'lucide-react'
import { FC, ReactNode, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { NavItem } from './NavItem'
import { RecentItemsList } from './RecentItemsList'
import { SidebarOverlay } from './SidebarOverlay'
import { CustomFlyoutProps, NavModule } from './types'

const appsNavItems: NavModule[] = [
    {
        id: 'all-applications',
        label: <FormattedMessage id="nav_all_applications" defaultMessage="All applications" />,
        icon: LayoutGrid,
        pathname: routes.applications.list,
        selectedPaths: [
            '/admin/applications/managed',
            // Exclude unmanaged and shadow apps from matching
        ],
        roles: [AppV1Appsviewer, AppV1Appseditor, AppV1Appsowner],
    },
]

const appsSecondaryItems: NavModule[] = [
    {
        id: 'unmanaged-apps',
        label: <FormattedMessage id="nav_unmanaged_apps" defaultMessage="Unmanaged apps" />,
        icon: Shapes,
        pathname: routes.applications.unmanagedApps,
        roles: [AppV1Appsviewer, AppV1Appseditor, AppV1Appsowner],
    },
    {
        id: 'shadow-apps',
        label: <FormattedMessage id="nav_shadow_apps" defaultMessage="Shadow apps" />,
        icon: HatGlasses,
        pathname: routes.applications.shadowApps,
        roles: [AppV1Appsviewer, AppV1Appseditor, AppV1Appsowner],
    },
]

export const AppsSidebarOverlay: FC<CustomFlyoutProps> = ({ onRef, label, isPersistent }) => {
    const hasRole = useRoleChecker()

    const visibleNavItems = useMemo(
        () => appsNavItems.filter((item) => !item.roles?.length || item.roles.some(hasRole)),
        [hasRole],
    )

    const visibleSecondaryItems = useMemo(
        () => appsSecondaryItems.filter((item) => !item.roles?.length || item.roles.some(hasRole)),
        [hasRole],
    )

    const hasAppsAccess = visibleNavItems.length > 0 || visibleSecondaryItems.length > 0

    return (
        <SidebarOverlay onRef={onRef} label={label} isPersistent={isPersistent}>
            <Box sx={{ py: 2 }}>
                {visibleNavItems.map((item) => (
                    <NavItem key={item.id} item={item} label={item.label} />
                ))}

                {/* Recent Apps - only show if user has apps access */}
                {hasAppsAccess && (
                    <RecentItemsList<string>
                        title={<FormattedMessage id="nav_recent_apps" defaultMessage="Recent" />}
                        category={RecentItemsCategory.APPS}
                        maxItems={10}
                        parseRef={(key): string => key}
                        getLink={(appId): ReturnType<typeof routes.applications.detail> =>
                            routes.applications.detail(appId)
                        }
                        renderItem={(appId): ReactNode => <AppDisplayNameWithFetch
                            appId={appId}
                            avatarSize="sm"
                            readOnly
                            maxWidth="100%"
                        />}
                    />
                )}

                {/* Divider - only show if there are secondary items */}
                {visibleSecondaryItems.length > 0 && (
                    <Box sx={{ my: 1, borderTop: '1px solid', borderTopColor: 'divider' }} />
                )}
                {visibleSecondaryItems.map((item) => (
                    <NavItem key={item.id} item={item} label={item.label} />
                ))}
            </Box>
        </SidebarOverlay>
    )
}
